import { useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Loader2, CheckCircle2 } from "lucide-react";
import { register, formatApiError } from "@/lib/api";

const BG =
  "https://images.unsplash.com/photo-1613665813446-82a78c468a1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVscyUyMGFlcmlhbCUyMHdoaXRlJTIwbW9kZXJufGVufDB8fHx8MTc4NjY0MjY2OXww&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Пароль має містити щонайменше 6 символів.");
      return;
    }
    if (password !== confirm) {
      setError("Паролі не збігаються.");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      setDone(true);
    } catch (err) {
      if (!err.response) {
        setError("Сервер недоступний. Перевірте зʼєднання.");
      } else {
        setError(formatApiError(err.response?.data?.detail) || "Помилка реєстрації");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-slate-100 px-4">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{ backgroundImage: `url(${BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-100/60 to-slate-100" />

      <div
        className="relative z-10 w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm"
        data-testid="register-card"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600">
            <Zap size={26} className="text-white" fill="white" />
          </span>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
            Реєстрація
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Створіть акаунт у порталі VIWAT Monitor
          </p>
        </div>

        {done ? (
          <div
            data-testid="register-success"
            className="flex flex-col items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center"
          >
            <CheckCircle2 size={40} className="text-emerald-500" />
            <div className="text-sm font-medium text-emerald-800">
              Реєстрацію отримано! Ваш акаунт буде активовано після підтвердження
              адміністратором. Після цього ви зможете увійти.
            </div>
            <Link
              to="/login"
              data-testid="register-back-login"
              className="mt-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Повернутися до входу
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Імʼя
              </label>
              <input
                data-testid="register-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="Ваше імʼя"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Email
              </label>
              <input
                data-testid="register-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Пароль
              </label>
              <input
                data-testid="register-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="Мінімум 6 символів"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Підтвердіть пароль
              </label>
              <input
                data-testid="register-password-confirm-input"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                data-testid="register-error"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600"
              >
                {error}
              </div>
            )}

            <button
              data-testid="register-submit-button"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              Зареєструватися
            </button>

            <p className="text-center text-sm text-slate-500">
              Вже маєте акаунт?{" "}
              <Link
                to="/login"
                data-testid="register-login-link"
                className="font-semibold text-blue-600 hover:text-blue-700"
              >
                Увійти
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
